import { Grid } from '~/engine/data/common/Grid';

/**
 * Fill an output volume's leading and trailing slices with copies of the
 * first and last slice that carry resampled data.
 *
 * The extension is defined in the OUTPUT's frame — a number of output
 * slices at each end — so it is applied here, to the output, rather than
 * by extending the sampler past the source. Asking the sampler to clamp
 * would have extended along the SOURCE's stacking axis, which is the
 * acquisition normal: the same as output Z for a transversal acquisition, and a
 * different axis entirely for an oblique, sagittal or coronal one. It also
 * could not tell the two ends apart, since a single clamp flag covers
 * both directions of an axis.
 *
 * Copying the boundary output slice also makes the recorded provenance
 * literally true: the replicas are copies of the slice the user can see at
 * the edge of the reformatted image, not a re-interpolation of whatever
 * the source held near its own edge.
 *
 * Mutates `pixels` in place. Both counts must leave at least one slice of
 * real data to copy from.
 */
export function replicateBoundarySlices(
    pixels: Int16Array,
    grid: Grid,
    inferiorSlices: number,
    superiorSlices: number
): void {
    if (inferiorSlices < 0 || superiorSlices < 0) {
        throw new Error(
            'replicateBoundarySlices: slice counts must be non-negative'
        );
    }
    if (inferiorSlices === 0 && superiorSlices === 0) {
        return;
    }

    const sliceSize = grid.sizeX * grid.sizeY;
    if (pixels.length !== sliceSize * grid.sizeZ) {
        throw new Error(
            'replicateBoundarySlices: pixel data length does not match the grid'
        );
    }

    const dataSlices = grid.sizeZ - inferiorSlices - superiorSlices;
    if (dataSlices < 1) {
        throw new Error(
            'replicateBoundarySlices: no resampled slice remains to copy from'
        );
    }

    // Inferior end: every slice below the first data slice is a copy of it.
    const firstData = pixels.subarray(
        inferiorSlices * sliceSize,
        (inferiorSlices + 1) * sliceSize
    );
    for (let z = 0; z < inferiorSlices; z++) {
        pixels.set(firstData, z * sliceSize);
    }

    // Superior end: likewise from the last data slice.
    const lastDataIndex = inferiorSlices + dataSlices - 1;
    const lastData = pixels.subarray(
        lastDataIndex * sliceSize,
        (lastDataIndex + 1) * sliceSize
    );
    for (let z = lastDataIndex + 1; z < grid.sizeZ; z++) {
        pixels.set(lastData, z * sliceSize);
    }
}
